/**
 * Shift Service
 * Manages VA work shifts for the check-in system
 */

import { supabase } from '../utils/supabase';
import timesheetCacheService from './timesheetCacheService';

// Constants
const CHECKIN_INTERVAL_MINUTES = 30;

/**
 * Start a new shift for a VA
 * @param {string} vaId - VA's user ID
 * @param {string} contractId - Contract ID for this shift
 * @param {string|null} projectId - Optional project ID for this shift
 * @param {Date|null} scheduledEndTime - Optional planned end time
 * @returns {Promise<{data: Object|null, error: Error|null}>}
 */
export const startShift = async (vaId, contractId, projectId = null, scheduledEndTime = null) => {
  try {
    // Check for existing active shift
    const { data: existingShift } = await supabase
      .from('va_shifts')
      .select('id, status')
      .eq('va_id', vaId)
      .eq('status', 'active')
      .single();

    if (existingShift) {
      return { 
        data: null, 
        error: new Error('You already have an active shift. Please end it before starting a new one.') 
      };
    }

    // Get employer from contract
    const { data: contract, error: contractError } = await supabase
      .from('contracts')
      .select('employer_id')
      .eq('id', contractId)
      .single();

    if (contractError || !contract) {
      return { data: null, error: new Error('Contract not found') };
    }

    const now = new Date();
    const expectedCheckins = scheduledEndTime 
      ? Math.ceil((new Date(scheduledEndTime) - now) / (CHECKIN_INTERVAL_MINUTES * 60 * 1000))
      : null;

    // First check-in should be due 30 minutes after shift starts, not immediately
    const firstCheckinDue = new Date(now.getTime() + CHECKIN_INTERVAL_MINUTES * 60 * 1000);

    // Create new shift
    const { data: shift, error: insertError } = await supabase
      .from('va_shifts')
      .insert({
        va_id: vaId,
        contract_id: contractId,
        project_id: projectId || null, // Add project_id if provided
        employer_id: contract.employer_id,
        start_time: now.toISOString(),
        scheduled_end_time: scheduledEndTime ? new Date(scheduledEndTime).toISOString() : null,
        expected_checkins: expectedCheckins,
        next_checkin_due: firstCheckinDue.toISOString(), // First check-in due in 30 minutes
        status: 'active'
      })
      .select()
      .single();

    if (insertError) throw insertError;

    return { data: shift, error: null };
  } catch (error) {
    console.error('Error starting shift:', error);
    return { data: null, error };
  }
};

/**
 * End an active shift
 * @param {string} shiftId - Shift ID to end
 * @returns {Promise<{data: Object|null, error: Error|null}>}
 */
export const endShift = async (shiftId) => {
  try {
    // First, fetch the shift details before updating
    const { data: shiftData, error: fetchError } = await supabase
      .from('va_shifts')
      .select(`
        id,
        va_id,
        contract_id,
        project_id,
        employer_id,
        start_time,
        status
      `)
      .eq('id', shiftId)
      .single();

    if (fetchError) throw fetchError;
    if (!shiftData || shiftData.status !== 'active') {
      throw new Error('Shift is not active');
    }

    // Update shift status
    const endTime = new Date().toISOString();
    const { data: shift, error: updateError } = await supabase
      .from('va_shifts')
      .update({
        status: 'completed',
        end_time: endTime
      })
      .eq('id', shiftId)
      .eq('status', 'active')
      .select()
      .single();

    if (updateError) throw updateError;

    // Calculate hours worked
    const startTime = new Date(shiftData.start_time);
    const endTimeDate = new Date(endTime);
    const hoursWorked = (endTimeDate - startTime) / (1000 * 60 * 60); // Convert ms to hours

    // Get contract details for hourly rate
    const { data: contractData, error: contractError } = await supabase
      .from('contracts')
      .select('hourly_rate')
      .eq('id', shiftData.contract_id)
      .single();

    if (contractError) {
      console.warn('Could not fetch contract details:', contractError);
    }

    const hourlyRate = contractData?.hourly_rate || 0;
    const totalAmount = hoursWorked * hourlyRate;

    // Create timesheet entry for this shift
    const dateStr = new Date(shiftData.start_time).toISOString().split('T')[0];
    const { error: timesheetError } = await supabase
      .from('timesheet_entries')
      .insert({
        shift_id: shiftId, // Link to the shift for timeline access
        contract_id: shiftData.contract_id,
        project_id: shiftData.project_id || null, // Include project if selected
        va_id: shiftData.va_id,
        employer_id: shiftData.employer_id,
        date: dateStr,
        start_time: shiftData.start_time,
        end_time: endTime,
        description: 'Shift time entry',
        hours_worked: parseFloat(hoursWorked.toFixed(2)),
        hourly_rate: hourlyRate,
        total_amount: parseFloat(totalAmount.toFixed(2)),
        status: 'pending', // Pending approval from employer
        is_billable: true
      });

    if (timesheetError) {
      console.error('[ShiftService] Critical error creating timesheet entry:', {
        error: timesheetError.message,
        code: timesheetError.code,
        details: timesheetError.details,
        hint: timesheetError.hint,
        vaId: shiftData.va_id,
        contractId: shiftData.contract_id,
        employerId: shiftData.employer_id
      });
      
      // Log to error tracking service for visibility
      throw new Error(`Failed to create timesheet entry for shift: ${timesheetError.message}`);
    } else {
      // Invalidate timesheet cache after successful entry creation
      console.log('[ShiftService] Successfully created timesheet entry, invalidating cache');
      await timesheetCacheService.invalidateAllTimesheetData(shiftData.va_id);
    }

    return { data: shift, error: null };
  } catch (error) {
    console.error('Error ending shift:', error);
    return { data: null, error };
  }
};

/**
 * Pause an active shift
 * @param {string} shiftId - Shift ID to pause
 * @param {string} reason - Optional reason for pausing
 * @returns {Promise<{data: Object|null, error: Error|null}>}
 */
export const pauseShift = async (shiftId, reason = '') => {
  try {
    const { data: shift, error } = await supabase
      .from('va_shifts')
      .update({
        status: 'paused',
        pause_reason: reason
      })
      .eq('id', shiftId)
      .eq('status', 'active')
      .select()
      .single();

    if (error) throw error;

    return { data: shift, error: null };
  } catch (error) {
    console.error('Error pausing shift:', error);
    return { data: null, error };
  }
};

/**
 * Resume a paused shift
 * @param {string} shiftId - Shift ID to resume
 * @returns {Promise<{data: Object|null, error: Error|null}>}
 */
export const resumeShift = async (shiftId) => {
  try {
    // When resuming, keep the original next_checkin_due time
    // The check-in schedule should not change due to a pause
    const { data: shift, error } = await supabase
      .from('va_shifts')
      .update({
        status: 'active',
        pause_reason: null
      })
      .eq('id', shiftId)
      .eq('status', 'paused')
      .select()
      .single();

    if (error) throw error;

    return { data: shift, error: null };
  } catch (error) {
    console.error('Error resuming shift:', error);
    return { data: null, error };
  }
};

/**
 * Abandon a shift (mark as incomplete)
 * @param {string} shiftId - Shift ID to abandon
 * @returns {Promise<{data: Object|null, error: Error|null}>}
 */
export const abandonShift = async (shiftId) => {
  try {
    const { data: shift, error } = await supabase
      .from('va_shifts')
      .update({
        status: 'abandoned',
        end_time: new Date().toISOString()
      })
      .eq('id', shiftId)
      .in('status', ['active', 'paused'])
      .select()
      .single();

    if (error) throw error;

    return { data: shift, error: null };
  } catch (error) {
    console.error('Error abandoning shift:', error);
    return { data: null, error };
  }
};

/**
 * Get the active shift for a VA
 * Includes both 'active' and 'paused' shifts since a paused shift is still ongoing
 * @param {string} vaId - VA's user ID
 * @returns {Promise<{data: Object|null, error: Error|null}>}
 */
export const getActiveShift = async (vaId) => {
  try {
    const { data: shifts, error } = await supabase
      .from('va_shifts')
      .select(`
        *,
        contracts!contract_id (
          id,
          hourly_rate,
          job_id,
          jobs!job_id (
            id,
            title
          )
        )
      `)
      .eq('va_id', vaId)
      .in('status', ['active', 'paused'])
      .order('start_time', { ascending: false })
      .limit(1);

    if (error) throw error;

    // Return first shift or null if no active shift
    const shift = shifts && shifts.length > 0 ? shifts[0] : null;
    return { data: shift, error: null };
  } catch (error) {
    console.error('Error getting active shift:', error);
    return { data: null, error };
  }
};

/**
 * Get shift history for a VA
 * @param {string} vaId - VA's user ID
 * @param {Object} options - Query options
 * @param {Date} options.startDate - Start date filter
 * @param {Date} options.endDate - End date filter
 * @param {number} options.limit - Max results
 * @returns {Promise<{data: Array|null, error: Error|null}>}
 */
export const getShiftHistory = async (vaId, options = {}) => {
  try {
    const { startDate, endDate, limit = 50 } = options;

    let query = supabase
      .from('va_shifts')
      .select(`
        *,
        contract_id,
        contracts!contract_id (
          id,
          hourly_rate,
          job_id,
          jobs!job_id (
            id,
            title
          ),
          employer_id
        )
      `)
      .eq('va_id', vaId)
      .order('start_time', { ascending: false })
      .limit(limit);

    if (startDate) {
      query = query.gte('start_time', new Date(startDate).toISOString());
    }

    if (endDate) {
      query = query.lte('start_time', new Date(endDate).toISOString());
    }

    const { data: shifts, error } = await query;

    if (error) throw error;

    return { data: shifts || [], error: null };
  } catch (error) {
    console.error('Error getting shift history:', error);
    return { data: null, error };
  }
};

/**
 * Get shift statistics for a VA
 * @param {string} vaId - VA's user ID
 * @param {Object} options - Date range options
 * @returns {Promise<{data: Object|null, error: Error|null}>}
 */
export const getShiftStats = async (vaId, options = {}) => {
  try {
    const { startDate, endDate } = options;
    
    let query = supabase
      .from('va_shifts')
      .select('*')
      .eq('va_id', vaId);

    if (startDate) {
      query = query.gte('start_time', new Date(startDate).toISOString());
    }

    if (endDate) {
      query = query.lte('start_time', new Date(endDate).toISOString());
    }

    const { data: shifts, error } = await query;

    if (error) throw error;

    // Calculate statistics
    const stats = {
      totalShifts: shifts.length,
      completedShifts: shifts.filter(s => s.status === 'completed').length,
      abandonedShifts: shifts.filter(s => s.status === 'abandoned').length,
      totalHours: shifts.reduce((sum, s) => sum + (parseFloat(s.total_hours) || 0), 0),
      totalCheckins: shifts.reduce((sum, s) => sum + (s.total_checkins || 0), 0),
      missedCheckins: shifts.reduce((sum, s) => sum + (s.missed_checkins || 0), 0),
      averageHoursPerShift: 0,
      checkinComplianceRate: 0
    };

    if (stats.completedShifts > 0) {
      stats.averageHoursPerShift = stats.totalHours / stats.completedShifts;
    }

    const totalExpected = stats.totalCheckins + stats.missedCheckins;
    if (totalExpected > 0) {
      stats.checkinComplianceRate = (stats.totalCheckins / totalExpected) * 100;
    }

    return { data: stats, error: null };
  } catch (error) {
    console.error('Error getting shift stats:', error);
    return { data: null, error };
  }
};

/**
 * Calculate time until next check-in is due
 * @param {Object} shift - Active shift object
 * @returns {Object} Time info { isOverdue, minutesUntilDue, secondsUntilDue }
 */
export const getTimeUntilNextCheckin = (shift) => {
  if (!shift || !shift.next_checkin_due) {
    return { isOverdue: false, minutesUntilDue: 0, secondsUntilDue: 0 };
  }

  const now = new Date();
  const dueTime = new Date(shift.next_checkin_due);
  const diffMs = dueTime - now;
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffMs / 60000);

  return {
    isOverdue: diffMs < 0,
    minutesUntilDue: diffMinutes,
    secondsUntilDue: diffSeconds,
    overdueMinutes: diffMs < 0 ? Math.abs(diffMinutes) : 0
  };
};

/**
 * Automatically end a shift that is overdue
 * Called when a shift is more than 30 minutes overdue
 * @param {string} shiftId - Shift ID to end
 * @returns {Promise<{data: Object|null, error: Error|null}>}
 */
export const autoEndOverdueShift = async (shiftId) => {
  try {
    // First, fetch the shift details
    const { data: shiftData, error: fetchError } = await supabase
      .from('va_shifts')
      .select(`
        id,
        va_id,
        contract_id,
        employer_id,
        start_time,
        status,
        next_checkin_due
      `)
      .eq('id', shiftId)
      .single();

    if (fetchError) throw fetchError;
    if (!shiftData || shiftData.status !== 'active') {
      return { data: null, error: new Error('Shift is not active') };
    }

    // Verify shift is actually overdue by 30+ minutes
    const now = new Date();
    const dueTime = new Date(shiftData.next_checkin_due);
    const overdueMinutes = Math.floor((now - dueTime) / (1000 * 60));

    if (overdueMinutes < 30) {
      return { data: null, error: new Error('Shift is not yet 30 minutes overdue') };
    }

    // Update shift status to abandoned with note
    const endTime = new Date().toISOString();
    const { data: shift, error: updateError } = await supabase
      .from('va_shifts')
      .update({
        status: 'abandoned',
        end_time: endTime,
        pause_reason: `Automatically ended: No check-in for ${overdueMinutes} minutes`
      })
      .eq('id', shiftId)
      .eq('status', 'active')
      .select()
      .single();

    if (updateError) throw updateError;

    // Calculate hours worked
    const startTime = new Date(shiftData.start_time);
    const endTimeDate = new Date(endTime);
    const hoursWorked = (endTimeDate - startTime) / (1000 * 60 * 60);

    // Get contract details for hourly rate
    const { data: contractData, error: contractError } = await supabase
      .from('contracts')
      .select('hourly_rate')
      .eq('id', shiftData.contract_id)
      .single();

    if (contractError) {
      console.warn('Could not fetch contract details:', contractError);
    }

    const hourlyRate = contractData?.hourly_rate || 0;
    const totalAmount = hoursWorked * hourlyRate;

    // Create timesheet entry marked as abandoned
    const dateStr = new Date(shiftData.start_time).toISOString().split('T')[0];
    const { error: timesheetError } = await supabase
      .from('timesheet_entries')
      .insert({
        shift_id: shiftId,
        contract_id: shiftData.contract_id,
        va_id: shiftData.va_id,
        employer_id: shiftData.employer_id,
        date: dateStr,
        start_time: shiftData.start_time,
        end_time: endTime,
        description: `Shift auto-ended: No check-in for ${overdueMinutes} minutes`,
        hours_worked: parseFloat(hoursWorked.toFixed(2)),
        hourly_rate: hourlyRate,
        total_amount: parseFloat(totalAmount.toFixed(2)),
        status: 'pending',
        is_billable: true
      });

    if (timesheetError) {
      console.warn('Could not create timesheet entry:', timesheetError);
    } else {
      // Invalidate timesheet cache after auto-end creates timesheet entry
      console.log('[ShiftService] Invalidating timesheet cache after auto-end');
      await timesheetCacheService.invalidateAllTimesheetData(shiftData.va_id);
    }

    // Create a notification for the VA
    await supabase
      .from('notifications')
      .insert({
        user_id: shiftData.va_id,
        title: 'Shift Automatically Ended',
        message: `Your shift was automatically ended after being ${overdueMinutes} minutes overdue for check-in. Please remember to check in regularly during your shifts.`,
        type: 'warning',
        data: {
          shift_id: shiftId,
          overdue_minutes: overdueMinutes,
          auto_ended: true
        }
      });

    return { data: shift, error: null };
  } catch (error) {
    console.error('Error auto-ending overdue shift:', error);
    return { data: null, error };
  }
};

/**
 * Subscribe to shift updates for real-time changes
 * @param {string} vaId - VA's user ID
 * @param {Function} callback - Callback for updates
 * @returns {Object} Supabase subscription
 */
export const subscribeToShiftUpdates = (vaId, callback) => {
  return supabase
    .channel(`va_shift_${vaId}`)
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'va_shifts',
        filter: `va_id=eq.${vaId}`
      },
      callback
    )
    .subscribe();
};

export default {
  startShift,
  endShift,
  pauseShift,
  resumeShift,
  abandonShift,
  getActiveShift,
  getShiftHistory,
  getShiftStats,
  getTimeUntilNextCheckin,
  subscribeToShiftUpdates,
  autoEndOverdueShift,
  CHECKIN_INTERVAL_MINUTES
};
